import { useEffect, useState } from 'react'
import { fetchSeasonSoFar, type SeasonSoFar } from '../data/season'
import { statType } from '../domain/statTypes'
import { TrendChart } from '../components/Charts'
import { formatDate } from '../lib/date'
import type { StatTypeId } from '../domain/types'

/**
 * Season So Far, read from the server so every member sees it -- not just the
 * device that scored. A thin renderer over season_so_far(); the numbers are the
 * database's.
 */
export function Season({ onClose }: { onClose?: () => void }) {
  const [data, setData] = useState<SeasonSoFar | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    fetchSeasonSoFar().then((r) => {
      if (!alive) return
      if (r.ok) setData(r.data)
      else setError(r.error)
    })
    return () => {
      alive = false
    }
  }, [])

  if (error) {
    return (
      <div className="panel">
        <div className="manage__note manage__note--error">Could not load Season So Far: {error}</div>
        {onClose && <button type="button" className="btn" onClick={onClose}>Back</button>}
      </div>
    )
  }
  if (!data) return <div className="boot"><div className="boot__spinner" /></div>

  if (data.games_scored === 0) {
    return (
      <div className="stats">
        {onClose && <button type="button" className="btn btn--sm" onClick={onClose}>← Back</button>}
        <section className="panel">
          <h2 className="panel__title">Season So Far</h2>
          <p className="muted">No games scored yet. This fills in after the first game is played and synced.</p>
        </section>
        {data.next_fixture && <NextFixture data={data} />}
        {data.ladder.length > 0 && <Ladder data={data} />}
      </div>
    )
  }

  return (
    <div className="stats">
      {onClose && <button type="button" className="btn btn--sm" onClick={onClose}>← Back</button>}
      <section className="panel">
        <h2 className="panel__title">Season So Far</h2>
        <p className="muted small">
          {data.games_scored} game{data.games_scored === 1 ? '' : 's'} scored · {data.season.grade_name ?? data.season.name}
        </p>
      </section>

      {data.most_improved.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Most improved</h2>
          <div className="rows">
            {data.most_improved.slice(0, 5).map((m, i) => (
              <div key={i} className="row">
                <div className="row__label">
                  <span className="row__title">{m.name}</span>
                  <span className="row__meta">
                    {m.full_name}: {m.recent_avg}/game lately vs {m.season_avg} for the season
                  </span>
                </div>
                <span className="row__go" style={{ color: colorOf(m.stat_type) }}>+{m.delta}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.leaderboards.map((lb) => (
        <section key={lb.stat_type} className="panel">
          <h2 className="panel__title" style={{ color: colorOf(lb.stat_type) }}>{lb.full_name}</h2>
          <div className="rows">
            {lb.players.slice(0, 8).map((p, i) => (
              <div key={p.name} className="row">
                <div className="row__label">
                  <span className="row__title">{i === 0 ? '★ ' : ''}{p.name}</span>
                  <span className="row__meta">{p.total} total · {p.per_game}/game</span>
                </div>
                <Sparkline values={p.series} color={colorOf(lb.stat_type)} />
              </div>
            ))}
          </div>
        </section>
      ))}

      {data.team_trend.length > 1 && (
        <section className="panel">
          <h2 className="panel__title">Team trend</h2>
          {data.leaderboards.map((lb) => {
            const points = data.team_trend.map((t) => ({
              label: t.round ?? String(t.game_number),
              value: t.totals?.[lb.stat_type] ?? 0,
            }))
            return (
              <div key={lb.stat_type} className="season__trend">
                <div className="muted small" style={{ color: colorOf(lb.stat_type) }}>{lb.full_name}</div>
                <TrendChart points={points} color={colorOf(lb.stat_type)} ariaLabel={`${lb.full_name} per game`} />
              </div>
            )
          })}
        </section>
      )}

      {data.by_result.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Wins vs losses</h2>
          <div className="rows">
            {data.by_result.map((r) => (
              <div key={r.stat_type} className="row">
                <div className="row__label">
                  <span className="row__title" style={{ color: colorOf(r.stat_type) }}>{r.full_name}</span>
                </div>
                <span className="row__meta">
                  {r.won_avg ?? '—'} in wins · {r.lost_avg ?? '—'} in losses
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.opponents.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Opponents</h2>
          <div className="rows">
            {data.opponents.map((o) => (
              <div key={o.opponent_name ?? '?'} className="row">
                <div className="row__label">
                  <span className="row__title">{o.opponent_name}</span>
                  <span className="row__meta">
                    {o.won}-{o.lost}{o.drawn ? `-${o.drawn}` : ''}
                    {o.averages && ' · ' + Object.entries(o.averages).map(([s, v]) => `${statType(s as StatTypeId).short} ${v}`).join(' · ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.finals.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">Finals</h2>
          <div className="rows">
            {data.finals.map((f, i) => (
              <div key={i} className="row">
                <div className="row__label">
                  <span className="row__title">{f.round} vs {f.opponent}</span>
                  <span className="row__meta">
                    {formatDate(f.date)}
                    {f.result ? ` · ${f.result} ${f.our_score}–${f.their_score}` : ` · ${f.status.toLowerCase()}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Ladder data={data} />
      {data.next_fixture && <NextFixture data={data} />}
    </div>
  )
}

function Ladder({ data }: { data: SeasonSoFar }) {
  if (data.ladder.length === 0) return null
  return (
    <section className="panel">
      <h2 className="panel__title">Ladder</h2>
      <div className="rows">
        {data.ladder.map((r) => (
          <div key={r.position} className={'row' + (r.is_us ? ' row--leader' : '')}>
            <div className="row__label">
              <span className="row__title">{r.position}. {r.team_name}{r.is_us ? ' (us)' : ''}</span>
              <span className="row__meta">
                {r.played}P · {r.won}W {r.lost}L{r.drawn ? ` ${r.drawn}D` : ''} · {r.competition_points ?? 0} pts
              </span>
            </div>
          </div>
        ))}
      </div>
      <p className="muted small">{data.grading_note}</p>
    </section>
  )
}

function NextFixture({ data }: { data: SeasonSoFar }) {
  const f = data.next_fixture!
  return (
    <section className="panel">
      <h2 className="panel__title">Next up</h2>
      <p>
        {f.round ? `${f.round} · ` : ''}
        {f.home_away === 'home' ? 'vs' : '@'} {f.opponent} · {formatDate(f.date)}
        {f.court_name ? ` · ${f.court_name}` : ''}
      </p>
    </section>
  )
}

/**
 * The per-player trend. The SVG stretches to fill its flex slot (sized in CSS:
 * wide-but-capped on desktop, compact on phones), so the row has no dead space
 * mid-line. preserveAspectRatio="none" lets the path stretch horizontally; the
 * stroke stays 2px via non-scaling-stroke so it never looks fat when wide.
 */
function Sparkline({ values, color }: { values: number[]; color: string }) {
  if (!values || values.length === 0) return null
  const W = 100
  const H = 26
  const max = Math.max(1, ...values)
  const x = (i: number) => (values.length === 1 ? W / 2 : (i / (values.length - 1)) * W)
  const y = (v: number) => H - 3 - (v / max) * (H - 6)
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  return (
    <svg
      className="row__spark"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={d} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1]!)} r={2.5} fill={color} />
    </svg>
  )
}

function colorOf(stat: string): string {
  return statType(stat as StatTypeId).color
}
